<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';
import { hasOneDayPassed } from 'shared/helpers/timeHelper';
import { MESSAGE_STATUS, ATTACHMENT_TYPES } from './constants';

const emit = defineEmits(['retry']);

const { status, createdAt, content, attachments, contentAttributes } =
  useMessageContext();

const { t } = useI18n();

// Aceleres: a janela de atendimento do Instagram/Messenger fecha 24h depois da
// ULTIMA mensagem recebida do contato. Estourado esse prazo o provedor recusa o
// envio com "This message is sent outside of allowed window." e o atendente via
// apenas "Falha ao enviar" -- indistinguivel de instabilidade, entao ele
// reenviava a mesma mensagem em loop, colecionando falhas.
//
// A checagem e pela string do provedor porque nada no payload identifica o
// motivo: `status` e `failed` para qualquer erro. Casar so por "allowed window"
// mantem o teste estreito o suficiente para nao capturar outros 400.
const OUTSIDE_WINDOW_ERROR_PATTERN = /outside of allowed window/i;

const isOutsideAllowedWindow = computed(() => {
  const externalError = String(
    contentAttributes.value?.externalError ??
      contentAttributes.value?.external_error ??
      ''
  );
  return OUTSIDE_WINDOW_ERROR_PATTERN.test(externalError);
});

const canRetry = computed(() => {
  // Reenviar fora da janela falha de novo, sempre: o bloqueio e do provedor e
  // so o contato o levanta, respondendo. Esconder o botao evita a sequencia de
  // tentativas identicas que o relato de 25/08/2026 descreve.
  if (isOutsideAllowedWindow.value) return false;

  const hasContent = content.value !== null;
  const hasAttachments = attachments.value && attachments.value.length > 0;
  return !hasOneDayPassed(createdAt.value) && (hasContent || hasAttachments);
});

// Aceleres: o erro cru do provedor (content_attributes.external_error, ex.:
// "Timed out reading data from server", "400 Bad Request") nunca chega ao
// atendente. Ele segue gravado na mensagem para o suporte consultar; aqui só
// mostramos um rótulo amigável escolhido pelo tipo de mídia que falhou.
const failureLabel = computed(() => {
  // O motivo vence o tipo de midia: saber que a janela fechou e mais util do
  // que saber que era uma imagem.
  if (isOutsideAllowedWindow.value) {
    return t('CHAT_LIST.FAILED_TO_SEND_OUTSIDE_WINDOW');
  }

  const attachment = attachments.value?.[0];
  if (!attachment) return t('CHAT_LIST.FAILED_TO_SEND');

  // Documentos enviados pela WAME podem chegar como áudio/imagem; a intenção
  // original em wame_media_type prevalece, igual à escolha da bolha feita em
  // Message.vue, para o rótulo não contradizer o card renderizado.
  const wameMediaType = String(
    contentAttributes.value?.wameMediaType ??
      contentAttributes.value?.wame_media_type ??
      ''
  ).toLowerCase();
  if (wameMediaType === 'document') return t('CHAT_LIST.FAILED_TO_SEND_FILE');

  switch (attachment.fileType) {
    case ATTACHMENT_TYPES.IMAGE:
      return t('CHAT_LIST.FAILED_TO_SEND_IMAGE');
    case ATTACHMENT_TYPES.VIDEO:
    case ATTACHMENT_TYPES.IG_REEL:
      return t('CHAT_LIST.FAILED_TO_SEND_VIDEO');
    case ATTACHMENT_TYPES.AUDIO:
      return t('CHAT_LIST.FAILED_TO_SEND_AUDIO');
    case ATTACHMENT_TYPES.FILE:
      return t('CHAT_LIST.FAILED_TO_SEND_FILE');
    default:
      return t('CHAT_LIST.FAILED_TO_SEND');
  }
});
</script>

<template>
  <div class="text-xs text-n-ruby-11 flex items-center gap-1.5">
    <Icon icon="i-lucide-alert-triangle" class="text-n-ruby-11 size-[14px]" />
    <span>{{ failureLabel }}</span>
    <button
      v-if="canRetry"
      type="button"
      :disabled="status !== MESSAGE_STATUS.FAILED"
      :title="t('CHAT_LIST.RETRY_SEND')"
      :aria-label="t('CHAT_LIST.RETRY_SEND')"
      class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      @click="emit('retry')"
    >
      <Icon icon="i-lucide-refresh-ccw" class="text-n-ruby-11 size-[14px]" />
    </button>
  </div>
</template>
